export const PDF_LESSONS = [
  {
    id: 'pdf-1',
    numero: 1,
    titre: "Ouvrir et lire un PDF",
    sousTitre: "Les bases",
    contexte: "Vous recevez un fichier PDF par email. Comment l'ouvrir et le lire correctement ?",
    explication: [
      "**Un PDF** (Portable Document Format) est un fichier qui conserve sa mise en forme sur tous les appareils. Il ressemble exactement à un document papier.",
      "**Pour l'ouvrir** : double-cliquez dessus. Il s'ouvre avec Adobe Reader, votre navigateur, ou l'application par défaut de votre ordinateur.",
      "**Sur téléphone** : appuyez sur le fichier, il s'ouvre directement. Sur iPhone avec l'app Fichiers, sur Android avec votre gestionnaire de fichiers.",
      "**Pour zoomer** : utilisez Ctrl + molette souris sur PC, ou pincez l'écran sur téléphone.",
      "**Astuce** : si le PDF ne s'ouvre pas, téléchargez Adobe Acrobat Reader gratuitement sur adobe.com — c'est le logiciel le plus fiable.",
    ],
    question: "Un PDF s'ouvre différemment selon l'appareil. Quelle affirmation est correcte ?",
    options: [
      { id: 'a', text: "Le PDF a toujours la même apparence, quel que soit l'appareil", correct: true },
      { id: 'b', text: "Le PDF change de mise en forme selon l'appareil", correct: false },
      { id: 'c', text: "Les PDF ne s'ouvrent que sur ordinateur", correct: false },
    ],
    explication_reponse: "C'est l'avantage principal du PDF : il conserve exactement la même apparence partout. Un formulaire CAF sur votre téléphone ressemble exactement au formulaire papier — c'est fait exprès.",
  },
  {
    id: 'pdf-2',
    numero: 2,
    titre: "Remplir un formulaire PDF",
    sousTitre: "Cliquer et taper",
    contexte: "La CAF vous envoie un formulaire PDF à remplir et retourner. Comment faire ?",
    explication: [
      "**Un formulaire PDF interactif** a des cases cliquables — vous cliquez dedans et vous tapez votre texte directement.",
      "**Pour naviguer** entre les champs : appuyez sur la touche Tab de votre clavier pour passer au champ suivant.",
      "**Les cases à cocher** : cliquez dessus pour cocher ou décocher. Une coche ou une croix apparaît.",
      "**Si les champs ne sont pas cliquables** : le PDF n'est pas interactif. Imprimez-le et remplissez-le à la main, ou utilisez un outil comme Adobe Acrobat.",
      "**Sauvegardez avant d'envoyer** : Fichier → Enregistrer sous → choisissez un nom clair comme 'Formulaire_CAF_Mars2026'.",
    ],
    question: "Vous cliquez sur un champ du PDF mais rien ne se passe. Que faire ?",
    options: [
      { id: 'a', text: "Imprimer le PDF et le remplir à la main", correct: true },
      { id: 'b', text: "Le PDF est cassé, demandez-en un autre", correct: false },
      { id: 'c', text: "Redémarrer l'ordinateur", correct: false },
    ],
    explication_reponse: "Si les champs ne sont pas interactifs, c'est un PDF 'image' — il faut l'imprimer et le remplir manuellement, ou utiliser un outil comme Adobe Acrobat pour annoter. Ce n'est pas un bug, certains organismes envoient encore des PDF non interactifs.",
  },
  {
    id: 'pdf-3',
    numero: 3,
    titre: "Signer un PDF",
    sousTitre: "La signature électronique",
    contexte: "Le formulaire demande une signature. Pas besoin d'imprimer — on peut signer directement.",
    explication: [
      "**Adobe Acrobat Reader** permet de signer gratuitement : ouvrez le PDF, cliquez sur 'Remplir et signer', puis sur 'Signer'.",
      "**3 options de signature** : taper votre nom (Adobe le transforme en signature), dessiner avec la souris ou le doigt, ou importer une photo de votre signature manuscrite.",
      "**Sur téléphone** : dessinez votre signature avec le doigt — c'est la méthode la plus simple et la plus naturelle.",
      "**La signature électronique est légalement valide** en France pour la plupart des démarches administratives.",
      "**Après avoir signé** : sauvegardez le PDF signé avec un nouveau nom pour ne pas écraser l'original.",
    ],
    question: "Quelle méthode de signature électronique est la plus simple sur téléphone ?",
    options: [
      { id: 'a', text: "Dessiner sa signature avec le doigt", correct: true },
      { id: 'b', text: "Imprimer, signer et scanner", correct: false },
      { id: 'c', text: "La signature électronique n'est pas acceptée", correct: false },
    ],
    explication_reponse: "Dessiner avec le doigt sur téléphone est la méthode la plus rapide et naturelle. La signature électronique est parfaitement légale en France — la loi l'accepte pour les démarches administratives courantes.",
  },
  {
    id: 'pdf-4',
    numero: 4,
    titre: "Envoyer un PDF par email",
    sousTitre: "Joindre et envoyer",
    contexte: "Votre formulaire est rempli et signé. Il faut maintenant l'envoyer par email à l'organisme.",
    explication: [
      "**Joindre un fichier** : dans votre messagerie, cherchez le trombone 📎 ou le bouton 'Joindre un fichier'. Cliquez dessus et retrouvez votre PDF.",
      "**Vérifiez le nom du fichier** avant d'envoyer — il doit être clair : 'Formulaire_CAF_NomPrenom_Mars2026.pdf', pas 'document123.pdf'.",
      "**Taille du fichier** : si votre PDF dépasse 5 Mo, il sera peut-être bloqué. Compressez-le sur ilovepdf.com (gratuit).",
      "**Accusé de réception** : demandez toujours une confirmation par email à l'organisme. Conservez cet email comme preuve d'envoi.",
      "**Conservez une copie** du PDF envoyé dans un dossier 'Documents importants' sur votre ordinateur ou votre téléphone.",
    ],
    question: "Vous devez envoyer un PDF de 8 Mo par email. Que faire ?",
    options: [
      { id: 'a', text: "Le compresser sur ilovepdf.com avant d'envoyer", correct: true },
      { id: 'b', text: "L'envoyer tel quel, la taille n'a pas d'importance", correct: false },
      { id: 'c', text: "Imprimer et envoyer par courrier postal à la place", correct: false },
    ],
    explication_reponse: "Un fichier trop lourd sera bloqué par les serveurs mail. ilovepdf.com est un outil gratuit et sécurisé qui réduit la taille des PDF en quelques secondes. Après compression, l'envoi par email se fait normalement.",
  },
];
